import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal, Button, Table, Badge, Form, InputGroup, FormControl, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { AppDispatch } from '../../../store';
import { toast } from 'react-toastify';
import { createRefundRequest, getAllRefundRequests, getUserRefundRequests, updateRefundStatus } from '../../../services/RefundRequest_service';
import RibModal from '../avoir/RibModal';
import { UserModel } from '../../../models/UserModel';
import '../../../css/ReferralWithdrawals.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faDownload, faEye, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

interface TeamProps {
    authUser: UserModel | null,
    fetchSecretary: () => void,
    secretary: any,
    setSecretary: (secretary: any) => void,
    onRefundRequestsUpdate?: (totalPending: number) => void,
}

function ReferralWithdrawals({ authUser, fetchSecretary, secretary, setSecretary, onRefundRequestsUpdate }: TeamProps) {
    const dispatch: AppDispatch = useDispatch();
    const [refundRequests, setRefundRequests] = useState<any[]>([]);
    const [showActorModal, setShowActorModal] = useState(false);
    const [showRibModal, setShowRibModal] = useState(false);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [requestDetails, setRequestDetails] = useState<any>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const [selectedActor, setSelectedActor] = useState<'parent' | 'secretary'>('parent');
    const [amount, setAmount] = useState<string>('');

    const [searchQuery, setSearchQuery] = useState<string>('');
    const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [includePdf, setIncludePdf] = useState<boolean>(false);

    const isAdmin = localStorage.getItem("role") === "ADMIN";
    const userId = localStorage.getItem("userId") || '';

    const fetchRequests = async () => {
        try {
            const response = isAdmin ? await getAllRefundRequests() : await getUserRefundRequests(userId);
            if (response.status === 200) {
                const data = response.data;
                setRefundRequests(data);

                const totalPending = data.reduce((acc: number, r: any) => {
                    return acc + (r.status === 'pending' ? Number(r.amount || 0) : 0);
                }, 0);
                if (typeof onRefundRequestsUpdate === 'function') {
                    onRefundRequestsUpdate(totalPending);
                }
            } else {
                toast.error('Erreur lors de la récupération des demandes de remboursement');
            }
        } catch (error) {
            toast.error('Erreur serveur');
        }
    };

    const refreshUserData = () => {
        fetchRequests();
        fetchSecretary();
    };

    useEffect(() => {
        fetchRequests();
        fetchSecretary();
    }, [isAdmin]);

    const hasRecentRefund = useMemo(() => {
        const now = new Date();
        const limitDate = new Date();
        limitDate.setDate(now.getDate() - 30);

        return refundRequests.some(r => {
            const d = new Date(r.created_at);
            return (
                d >= limitDate &&
                (r.status === 'pending' || r.status === 'accepted')
            );
        });
    }, [refundRequests]);

    const handleRefundRequest = () => {
        const parentBalance = authUser?.solde || 0;
        const secretaryBalance = secretary?.solde || 0;

        if (parentBalance < 50 && secretaryBalance < 50) {
            toast.error("Aucun solde suffisant pour un retrait (minimum 50€).");
            return;
        }
        setShowActorModal(true);
    };

    const handleActorSelection = (actor: 'parent' | 'secretary') => {
        setSelectedActor(actor);
        setErrorMessage('');
    };

    const handleRibConfirm = async (rib: string, bic?: string) => {
        setIsLoading(true);
        setShowRibModal(false);

        try {
            const numericAmount = parseFloat(amount || '0');
            const requestData: any = {
                actor: selectedActor,
                rib,
                bic: bic || null,
                amount: numericAmount,
                user_id: authUser?.id,
                entreprise_team_id: selectedActor === 'secretary' ? secretary?.id : null
            };

            const response = await createRefundRequest(requestData);
            if (response.status === 201) {
                toast.success('Demande de remboursement soumise. Le montant a été déduit de votre solde.');
                setShowActorModal(false);
                setAmount('');
                refreshUserData();
            } else {
                toast.error(response?.data?.message || 'Erreur lors de la soumission');
            }
        } catch (error: any) {
            const message = error.response?.data?.message || 'Erreur lors de la soumission';
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleRejectClick = (requestId: number) => {
        setSelectedRequestId(requestId);
        setShowRejectionModal(true);
    };

    const handleRejectConfirm = async () => {
        if (!selectedRequestId || !rejectionReason.trim()) {
            toast.error('Veuillez saisir un motif de refus');
            return;
        }

        setStatusLoading(true);
        try {
            const response = await updateRefundStatus(selectedRequestId, 'rejected', rejectionReason);
            if (response.status === 200) {
                toast.success('Demande rejetée. Le montant a été remis dans le solde.');
                setShowRejectionModal(false);
                setRejectionReason('');
                setSelectedRequestId(null);
                refreshUserData();
            } else {
                toast.error('Erreur lors du rejet');
            }
        } catch (error) {
            toast.error('Erreur lors du rejet');
        } finally {
            setStatusLoading(false);
        }
    };

    const handleChangeStatus = async (requestId: number, status: 'accepted') => {
        setStatusLoading(true);
        try {
            const response = await updateRefundStatus(requestId, status);
            if (response.status === 200) {
                toast.success('Demande acceptée avec succès');
                refreshUserData();
            } else {
                toast.error('Erreur lors de la mise à jour du statut');
            }
        } catch (error) {
            toast.error('Erreur lors de la mise à jour du statut');
        } finally {
            setStatusLoading(false);
        }
    };

    const showDetail = (request: any) => {
        setRequestDetails(request);
        setShowDetailsModal(true);
    };

    const formatDate = (dateString: string | undefined) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('fr-FR');
        } catch {
            return 'Invalid Date';
        }
    };

    const formatDateTime = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatRequestNumber = (requestNumber: string | null) => {
        return requestNumber || 'N/A';
    };

    const getStatusBadge = (status: string) => {
        const mapping: Record<string, { className: string; text: string }> = {
            pending: { className: 'badgePending', text: 'En attente' },
            accepted: { className: 'badge-primary-light', text: 'Acceptée' },
            rejected: { className: 'badge-accent', text: 'Rejetée' },
        };
        const cfg = mapping[status] || { className: 'badge-accent', text: status };
        return <Badge className={cfg.className}>{cfg.text}</Badge>;
    };

    const onChangeAmountInput = (raw: string) => {
        let v = raw.replace(',', '.');
        v = v.replace(/[^\d.]/g, '');
        const parts = v.split('.');
        if (parts.length > 1) {
            v = parts[0] + '.' + parts.slice(1).join('').slice(0, 2);
        }
        if (v.length > 1 && !v.startsWith('0.')) {
            v = v.replace(/^0+/, '');
        }
        setAmount(v);
        setErrorMessage('');
    };

    const numericAmount = () => parseFloat(amount || '0');

    const handleContinue = () => {
        const num = numericAmount();
        if (selectedActor === "parent" && authUser && authUser.solde && num > authUser.solde) {
            setErrorMessage('Le solde du parent est insuffisant!');
        } else if (selectedActor === "secretary" && secretary && secretary.solde && num > secretary.solde) {
            setErrorMessage('Le solde de l\'assistant est insuffisant!');
        } else if (num < 50) {
            setErrorMessage('Merci de bien vouloir saisir un montant supérieur à 50 €.');
        } else {
            setShowActorModal(false);
            setShowRibModal(true);
        }
    };

    const buildAttestationUrl = (path: string | null): string | undefined => {
        if (!path) return undefined;
        const apiBase = process.env.REACT_APP_API_BASE!;
        const cleanPath = path.startsWith("/") ? path.slice(1) : path;
        return `${apiBase}/${cleanPath}`;
    };

    const buildDownloadUrl = (requestId: number): string => {
        const apiBase = process.env.REACT_APP_API_BASE!;
        return `${apiBase}/refund-requests/${requestId}/download-attestation`;
    };

    const filteredRequests = useMemo(() => {
        let items = [...refundRequests];

        if (searchQuery && searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            items = items.filter((r: any) => {
                const number = (r.request_number || '').toString().toLowerCase();
                const userFull = r.User ? (`${r.User.firstname || ''} ${r.User.name || ''}`).toLowerCase() : '';
                const teamFull = r.EntrepriseTeam ? (`${r.EntrepriseTeam?.prenom || ''} ${r.EntrepriseTeam?.nom || ''}`).toLowerCase() : '';
                const email = (r.User?.email || r.EntrepriseTeam?.email || '').toLowerCase();
                return number.includes(q) || userFull.includes(q) || teamFull.includes(q) || email.includes(q);
            });
        }

        if (dateRange && dateRange.start && dateRange.end) {
            const start = new Date(dateRange.start);
            const end = new Date(dateRange.end);
            start.setHours(0, 0, 0, 0);
            end.setHours(23, 59, 59, 999);
            items = items.filter((r: any) => {
                const d = new Date(r.created_at);
                return d >= start && d <= end;
            });
        }

        return items.sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [refundRequests, searchQuery, dateRange]);

    if (!authUser) {
        return <div>Veuillez vous authentifier!</div>;
    }

    const toggleSelect = (id: number) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleSelectAllVisible = (checked: boolean) => {
        if (checked) {
            setSelectedIds(filteredRequests.map(r => r.id));
        } else {
            setSelectedIds([]);
        }
    };

    const exportRowsFor = (mode: 'all' | 'filtered' | 'selected') => {
        let items: any[] = [];
        if (mode === 'all') items = [...refundRequests];
        else if (mode === 'filtered') items = [...filteredRequests];
        else if (mode === 'selected') items = refundRequests.filter(r => selectedIds.includes(r.id));
        return items;
    };

    const buildExcelData = (items: any[]) => {
        return items.map((r: any) => ({
            'Numéro': r.request_number || 'N/A',
            'Date': formatDate(r.created_at),
            'Nom et prénom': r.actor === 'parent' ? (r.User?.is_entreprise ? r.User?.raison_social : `${r.User?.firstname || ''} ${r.User?.name || ''}`) : `${r.EntrepriseTeam?.prenom || ''} ${r.EntrepriseTeam?.nom || ''}`,
            'Montant': Number(r.amount).toFixed(2) + ' €',
            'AttestationPath': r.attestation_path || ''
        }));
    };

    const exportExcel = async (mode: 'all' | 'filtered' | 'selected') => {
        const items = exportRowsFor(mode);
        if (mode === 'selected' && items.length === 0) {
            toast.warn('Aucune demande sélectionnée pour l\'export.');
            return;
        }

        const rows = buildExcelData(items);
        const ws = XLSX.utils.json_to_sheet(rows.map(r => ({ 'Numéro': r['Numéro'], 'Date': r['Date'], 'Nom et prénom': r['Nom et prénom'], 'Montant': r['Montant'] })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Remboursements');
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });

        if (!includePdf) {
            const blob = new Blob([wbout], { type: 'application/octet-stream' });
            saveAs(blob, `remboursements_${mode}_${new Date().toISOString().slice(0, 10)}.xlsx`);
            toast.success('Export Excel téléchargé');
            return;
        }

        try {
            const zip = new JSZip();
            zip.file(`remboursements_${mode}_${new Date().toISOString().slice(0, 10)}.xlsx`, wbout);

            const pdfPromises = items.map(async (r: any) => {
                if (!r.attestation_path) return null;
                const url = buildAttestationUrl(r.attestation_path);
                if (!url) return null;
                try {
                    const res = await fetch(url);
                    if (!res.ok) return null;
                    const blob = await res.blob();
                    const fileName = `${r.request_number || r.id}_attestation.pdf`;
                    zip.file(fileName, blob);
                    return fileName;
                } catch (err) {
                    console.warn('Impossible de récupérer PDF:', err);
                    return null;
                }
            });

            await Promise.all(pdfPromises);

            const content = await zip.generateAsync({ type: 'blob' });
            saveAs(content, `remboursements_${mode}_avec_pdfs_${new Date().toISOString().slice(0, 10)}.zip`);
            toast.success('Export (Excel + PDF) téléchargé');
        } catch (err) {
            console.error(err);
            toast.error('Erreur lors de la création de l\'archive (vérifier CORS et URLs des PDFs)');
        }
    };

    const determineExportMode = (): 'selected' | 'filtered' | 'all' => {
        if (selectedIds && selectedIds.length > 0) return 'selected';
        if (searchQuery && searchQuery.trim()) return 'filtered';
        if (dateRange && (dateRange.start || dateRange.end)) return 'filtered';
        return 'all';
    };

    const handleExportClick = () => {
        const mode = determineExportMode();
        exportExcel(mode);
    };

    return (
        <div className="container">
            {!isAdmin && (
                <div className="mb-4">
                    <div className="row">
                        <div className="col-md-12 d-flex gap-3">
                            <div className="card col-md-6 balance-card-parent">
                                <div className="card-body">
                                    <h6 className="card-title">Solde Parent</h6>
                                    <h4>{(authUser.solde || 0).toFixed(2)}€</h4>
                                </div>
                            </div>
                            {secretary && (
                                <div className="col-md-6">
                                    <div className="card balance-card-secretary">
                                        <div className="card-body">
                                            <h6 className="card-title">Solde Assistant</h6>
                                            <h4>{(secretary.solde || 0).toFixed(2)}€</h4>
                                            <small className="text-muted">{secretary.firstname} {secretary.name}</small>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <OverlayTrigger
                        placement="right"
                        overlay={
                            hasRecentRefund ? (
                                <Tooltip id="refund-tooltip-client">
                                    Vous pouvez effectuer un remboursement tous les 30 jours à compter de votre dernier retrait.
                                </Tooltip>
                            ) : (
                                <Tooltip id="refund-tooltip-client">
                                    Montant minimum : 50 €. Un remboursement autorisé tous les 30 jours.
                                </Tooltip>
                            )
                        }
                    >
                        <span className="d-inline-block">
                            <Button
                                className="btn-accent mt-3"
                                onClick={handleRefundRequest}
                                disabled={isLoading || hasRecentRefund}
                                style={hasRecentRefund ? { pointerEvents: 'none' } : undefined}
                            >
                                Demander un remboursement
                            </Button>
                        </span>
                    </OverlayTrigger>
                </div>
            )}

            <h5>Liste des demandes de remboursement</h5>

            {isAdmin && (
                <div className="mb-3 d-flex align-items-center gap-2 toolbar-export flex-wrap">
                    <InputGroup style={{ maxWidth: 420 }}>
                        <FormControl
                            placeholder="Recherche par numéro, nom, email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Button variant="outline-secondary" onClick={() => setSearchQuery('')}>Clear</Button>
                    </InputGroup>

                    <div className="d-flex gap-2 align-items-center">
                        <FormControl
                            type="date"
                            value={dateRange?.start || ''}
                            onChange={(e) => setDateRange(prev => ({ start: e.target.value, end: prev?.end || '' }))}
                            placeholder="Date début"
                            style={{ width: '155px' }}
                        />
                        <span className="text-muted">à</span>
                        <FormControl
                            type="date"
                            value={dateRange?.end || ''}
                            onChange={(e) => setDateRange(prev => ({ start: prev?.start || '', end: e.target.value }))}
                            placeholder="Date fin"
                            style={{ width: '155px' }}
                        />
                        {dateRange && (dateRange.start || dateRange.end) && (
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setDateRange(null)}
                                title="Réinitialiser les dates"
                            >
                                ✕
                            </Button>
                        )}
                    </div>

                    <Form.Check
                        type="checkbox"
                        label="Inclure PDF"
                        checked={includePdf}
                        onChange={(e) => setIncludePdf(e.target.checked)}
                    />

                    <Button
                        onClick={handleExportClick}
                        className="btn-primary-dark gap-1"
                    >
                        <FontAwesomeIcon icon={faFileExcel} />
                        Exporter
                    </Button>
                </div>
            )}

            <Table striped bordered hover responsive>
                <thead className="table-header-custom">
                    <tr>
                        {isAdmin && (
                            <th style={{ width: 40 }}>
                                <Form.Check
                                    type="checkbox"
                                    checked={selectedIds.length > 0 && selectedIds.length === filteredRequests.length}
                                    onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                                />
                            </th>
                        )}
                        <th>N° Demande</th>
                        <th>Date</th>
                        <th>Demandeur</th>
                        {isAdmin && <th>Type</th>}
                        <th>Montant</th>
                        <th>RIB</th>
                        <th>Statut</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredRequests.length > 0 ? (
                        filteredRequests.map((request: any) => (
                            <tr key={request.id}>
                                {isAdmin && (
                                    <td>
                                        <Form.Check
                                            type="checkbox"
                                            checked={selectedIds.includes(request.id)}
                                            onChange={() => toggleSelect(request.id)}
                                        />
                                    </td>
                                )}
                                <td>
                                    <code className="badge badge-primary-light">{formatRequestNumber(request.request_number)}</code>
                                </td>
                                <td>{formatDate(request.created_at)}</td>
                                <td>
                                    {request.actor === 'parent' ? (
                                        <div>
                                            <strong>{request.User.is_entreprise ? request.User.raison_social : `${request.User.firstname} ${request.User.name}`}</strong>
                                            <br />
                                            <small className="text-muted">{request.User.email}</small>
                                        </div>
                                    ) : (
                                        <div>
                                            <strong>{request.EntrepriseTeam?.prenom} {request.EntrepriseTeam?.nom}</strong>
                                            <br />
                                            <small className="text-muted">{request.EntrepriseTeam?.email}</small>
                                            <br />
                                            <Badge className="badge-primary-light mt-1">Assistant</Badge>
                                        </div>
                                    )}
                                </td>
                                {isAdmin && (
                                    <td>
                                        <Badge className={request.actor === 'parent' ? 'badge-primary-light' : 'badge-primary-dark'}>
                                            {request.actor === 'parent' ? 'Parent' : 'Assistant'}
                                        </Badge>
                                    </td>
                                )}
                                <td className="fw-bold">{Number(request.amount).toFixed(2)} €</td>
                                <td>
                                    <code className="rib-code">{request.rib ? `${request.rib.slice(0, 4)}****${request.rib.slice(-4)}` : 'N/A'}</code>
                                </td>
                                <td>
                                    {getStatusBadge(request.status)}
                                    {request.rejection_reason && (
                                        <div className="text-accent small mt-1">Motif: {request.rejection_reason}</div>
                                    )}
                                </td>
                                <td>
                                    {!isAdmin ? (
                                        request.status === 'pending' ? null : (
                                            <Button size="sm" variant="" onClick={() => showDetail(request)} title="Détails">
                                                <FontAwesomeIcon icon={faEye} style={{ color: '#2A919D', border: '1px solid #2A919D', borderRadius: '6px', padding: '6px 8px', cursor: 'pointer' }} />
                                            </Button>
                                        )
                                    ) : (
                                        <div className="d-flex gap-2">
                                            <Button className="btn-info-custom" size="sm" onClick={() => showDetail(request)}>Détails</Button>
                                            {request.status === 'pending' && (
                                                <>
                                                    <Button className="btn-success-custom" size="sm" onClick={() => handleChangeStatus(request.id, 'accepted')} disabled={statusLoading}>{statusLoading ? 'Chargement...' : 'Accepter'}</Button>
                                                    <Button className="btn-danger-custom" size="sm" onClick={() => handleRejectClick(request.id)} disabled={statusLoading}>Rejeter</Button>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan={isAdmin ? 9 : 8} className="text-center">Aucune demande de remboursement pour le moment</td>
                        </tr>
                    )}
                </tbody>
            </Table>

            <Modal show={showActorModal} onHide={() => setShowActorModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Choisir le compte à rembourser</Modal.Title>
                </Modal.Header>
                <Modal.Body className="compact">
                    <div className="d-grid gap-3">
                        <div role="button" onClick={() => handleActorSelection('parent')} className={`select-card ${selectedActor === 'parent' ? 'selected' : ''}`} aria-disabled={(authUser?.solde || 0) < 50}>
                            <div className="title">Mon compte</div>
                            <div className="balance">Solde: {(authUser?.solde || 0).toFixed(2)}€</div>
                        </div>

                        {secretary && (
                            <div role="button" onClick={() => handleActorSelection('secretary')} className={`select-card ${selectedActor === 'secretary' ? 'selected' : ''}`} aria-disabled={(secretary?.solde || 0) < 50}>
                                <div className="title">Compte Assistant</div>
                                <div className="text-muted">{secretary.firstname} {secretary.name}</div>
                                <div className="balance">Solde: {(secretary?.solde || 0).toFixed(2)}€</div>
                            </div>
                        )}

                        <Form.Group>
                            <Form.Label>Montant à rembourser</Form.Label>
                            <InputGroup>
                                <FormControl type="text" inputMode="decimal" pattern="[0-9]*[.,]?[0-9]*" placeholder="Ex: 150.50" className="input-amount" value={amount} onChange={e => onChangeAmountInput(e.target.value)} required />
                            </InputGroup>
                            {errorMessage && <Form.Label className="error-message">{errorMessage}</Form.Label>}
                        </Form.Group>
                    </div>
                </Modal.Body>
                <Modal.Footer className="footer-actions">
                    <Button onClick={() => setShowActorModal(false)} className="btn-outline">Annuler</Button>
                    <Button onClick={handleContinue} className="btn-continue">Continuer</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showRejectionModal} onHide={() => setShowRejectionModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Motif du refus</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Veuillez indiquer le motif du refus *</Form.Label>
                        <Form.Control as="textarea" rows={4} value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Ex: Informations bancaires incorrectes, solde insuffisant après vérification..." required />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className='footer-actions'>
                    <Button onClick={() => setShowRejectionModal(false)} className="btn-outline">Annuler</Button>
                    <Button onClick={handleRejectConfirm} disabled={statusLoading || !rejectionReason.trim()} className="btn-continue">{statusLoading ? 'Chargement...' : 'Confirmer le refus'}</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{isAdmin ? "Détails de la demande de remboursement" : "Attestation de votre remboursement"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {requestDetails && (
                        <div>
                            <div className="alert alert-info mb-3">
                                <strong>Numéro de demande:</strong>
                                <h5 className="mb-0 mt-1">
                                    <code style={{ fontSize: '1.2rem', color: '#2A919D' }}>{formatRequestNumber(requestDetails.request_number)}</code>
                                </h5>
                            </div>

                            {!isAdmin ? (
                                <div>
                                    <div className="mb-3">
                                        {requestDetails.attestation_path ? (
                                            <>
                                                <div className="my-2">
                                                    <object data={buildAttestationUrl(requestDetails.attestation_path)} type="application/pdf" width="100%" height="480">
                                                        <p>Votre navigateur ne peut pas afficher le PDF. Vous pouvez le télécharger via le bouton ci-dessous.</p>
                                                    </object>
                                                </div>

                                                <div className="d-flex gap-2">
                                                    <a href={buildAttestationUrl(requestDetails.attestation_path) || '#'} target="_blank" rel="noopener noreferrer" style={{ color: '#2A919D', border: '1px solid #2A919D', borderRadius: '6px', padding: '6px 8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontStyle: 'bold' }}>
                                                        <FontAwesomeIcon icon={faFilePdf} style={{ color: '#2A919D' }} /> Voir dans un nouvel onglet
                                                    </a>

                                                    <a href={buildDownloadUrl(requestDetails.id)} className="btn btn-primary">
                                                        <FontAwesomeIcon icon={faDownload} /> Télécharger l'attestation
                                                    </a>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-muted mt-2">Aucune attestation disponible pour le moment.</div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <strong>Date et heure de demande:</strong>
                                            <div className="mt-1">{formatDateTime(requestDetails.created_at)}</div>
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Statut:</strong> {getStatusBadge(requestDetails.status)}
                                        </div>
                                        <div className="col-md-3">
                                            <strong>Type:</strong>
                                            <Badge className={requestDetails.actor === 'parent' ? 'badge-primary-light ms-2' : 'badge-primary-dark ms-2'}>
                                                {requestDetails.actor === 'parent' ? 'Parent' : 'Assistant'}
                                            </Badge>
                                        </div>
                                    </div>

                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <strong>Montant:</strong> {Number(requestDetails.amount).toFixed(2)} €
                                        </div>
                                        <div className="col-md-6 d-flex flex-column">
                                            <div>
                                                <strong>RIB:</strong>
                                                <code className="rib-code ms-2">{requestDetails.rib || 'Non fourni'}</code>
                                            </div>
                                            <div>
                                                <strong>BIC:</strong>
                                                <code className="rib-code ms-2">{requestDetails.bic || 'Non fourni'}</code>
                                            </div>
                                        </div>
                                    </div>

                                    {requestDetails.rejection_reason && (
                                        <div className="alert alert-primary-dark">
                                            <strong>Motif du refus:</strong>
                                            <p className="mb-0 mt-2">{requestDetails.rejection_reason}</p>
                                        </div>
                                    )}

                                    <div className="mb-3">
                                        <strong>Demandeur:</strong>
                                        <div className="mt-2">
                                            {requestDetails.actor === 'parent' ? (
                                                <div>
                                                    <div className="fw-bold">
                                                        {requestDetails.User?.is_entreprise ? requestDetails.User?.raison_social : `${requestDetails.User?.firstname || ''} ${requestDetails.User?.name || ''}`}
                                                    </div>
                                                    <div className="text-muted">{requestDetails.User?.email || 'N/A'}</div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <div className="fw-bold">
                                                        {requestDetails.EntrepriseTeam?.prenom} {requestDetails.EntrepriseTeam?.nom}
                                                    </div>
                                                    <div className="text-muted">{requestDetails.EntrepriseTeam?.email}</div>
                                                    <Badge className="badge-primary-light mt-1">
                                                        Assistant de {requestDetails.User?.is_entreprise ? requestDetails.User?.raison_social : `${requestDetails.User?.firstname || ''} ${requestDetails.User?.name || ''}`}
                                                    </Badge>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {requestDetails.status === 'accepted' && (
                                        <div className="alert alert-primary-light">
                                            <strong>✓ Remboursement effectué</strong>
                                            <p className="mb-0">Le montant a été traité.</p>
                                        </div>
                                    )}

                                    {requestDetails.status === 'rejected' && (
                                        <div className="alert alert-primary-dark">
                                            <strong>✗ Demande rejetée</strong>
                                            <p className="mb-0">Le montant a été remis dans le solde.</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            <RibModal visible={showRibModal} onCancel={() => setShowRibModal(false)} onConfirm={handleRibConfirm} loading={isLoading} requireBic={true} />
        </div>
    );
}

export default ReferralWithdrawals;
